/**
 * Convert any string to a URL-safe kebab-case slug.
 * Handles accents, special chars (*, ", etc.), spaces, multiple dashes.
 * Must match Laravel's Str::slug() output for sitemap/canonical consistency.
 */
export function slugify(str) {
  if (!str) return '';
  return String(str)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')       // strip accents
    .replace(/[*"'\r\n\t\\|<>]/g, ' ')     // strip problematic chars
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')         // keep only safe chars
    .trim()
    .replace(/\s+/g, '-')                  // spaces → dash
    .replace(/-+/g, '-')                   // collapse multiple dashes
    .slice(0, 80);                         // cap length
}

/**
 * Build a canonical car URL (must match sitemap.xml output)
 */
export function carUrl(car, base = 'https://selected.sk') {
  const make = slugify(car.brand || car.make);
  const model = slugify(car.model);
  const year = car.year || 'new';
  return `${base}/car/${make}/${model}/${year}/${car.id}`;
}

/**
 * SEO helper - updates all meta tags per page for optimal search engine visibility
 *
 * @param {Object} options
 * @param {string} options.title - Page title
 * @param {string} options.description - Meta description (max 160 chars ideal)
 * @param {string} [options.canonical] - Canonical URL (defaults to current path)
 * @param {string} [options.image] - OG image URL
 * @param {string} [options.keywords] - Meta keywords
 * @param {string} [options.type] - OG type (default: website)
 * @param {Object[]} [options.breadcrumbs] - Breadcrumb items [{name, url}]
 * @param {Object} [options.schema] - Additional JSON-LD schema to inject
 */
export function setPageSeo(options) {
  // Support legacy call: setPageSeo(title, description)
  if (typeof options === 'string') {
    options = { title: options, description: arguments[1] || '' };
  }

  const {
    title,
    description = '',
    canonical,
    image,
    keywords,
    type = 'website',
    breadcrumbs,
    schema
  } = options;

  const baseUrl = 'https://selected.sk';
  const currentPath = window.location.pathname;
  const canonicalUrl = canonical || (baseUrl + currentPath);
  const ogImage = image || baseUrl + '/frontend/assets/images/og-image.jpg';

  // Title
  document.title = title;

  // Meta description
  setMeta('name', 'description', description);

  // Meta keywords
  if (keywords) {
    setMeta('name', 'keywords', keywords);
  }

  // Canonical
  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = canonicalUrl;

  // Open Graph
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', canonicalUrl);
  setMeta('property', 'og:image', ogImage);
  setMeta('property', 'og:type', type);

  // Twitter Card
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', ogImage);

  // Breadcrumbs JSON-LD
  if (breadcrumbs && breadcrumbs.length) {
    injectJsonLd('seo-breadcrumbs', {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": item.name,
        "item": item.url.startsWith('http') ? item.url : baseUrl + item.url
      }))
    });
  } else {
    removeJsonLd('seo-breadcrumbs');
  }

  // Additional schema
  if (schema) {
    injectJsonLd('seo-page-schema', schema);
  } else {
    removeJsonLd('seo-page-schema');
  }
}

/**
 * Helper: set or create a meta tag
 */
function setMeta(attr, name, content) {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (el) {
    el.setAttribute('content', content);
  } else {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    el.content = content;
    document.head.appendChild(el);
  }
}

/**
 * Helper: inject or update a JSON-LD script block by id
 */
function injectJsonLd(id, data) {
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

/**
 * Helper: remove a JSON-LD script block by id
 */
function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/**
 * Generate Vehicle schema for car detail pages
 */
export function setVehicleSeo(car, primaryImageUrl) {
  const baseUrl = 'https://selected.sk';
  const carUrl = `${baseUrl}/car/${slugify(car.brand)}/${slugify(car.model)}/${car.year || 'new'}/${car.id}`;
  const price = car.nettoPrice || car.price;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Car",
    "name": `${car.year} ${car.brand} ${car.model}${car.trimLine ? ' ' + car.trimLine : ''}`,
    "manufacturer": {
      "@type": "Organization",
      "name": car.brand
    },
    "model": car.model,
    "vehicleModelDate": String(car.year),
    "url": carUrl,
    "image": primaryImageUrl || `${baseUrl}/frontend/assets/images/og-image.jpg`,
    "description": `${car.year} ${car.brand} ${car.model}${car.trimLine ? ' ' + car.trimLine : ''}. Premium European vehicle available at Selected Cars.`,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "EUR",
      "price": price,
      "availability": car.status === 'sold'
        ? "https://schema.org/SoldOut"
        : "https://schema.org/InStock",
      "seller": {
        "@type": "AutoDealer",
        "name": "Selected Cars",
        "url": baseUrl
      }
    }
  };

  // Add optional fields
  if (car.mileage) schema.mileageFromOdometer = {
    "@type": "QuantitativeValue",
    "value": car.mileage,
    "unitCode": "KMT"
  };
  if (car.fuelType) schema.fuelType = car.fuelType;
  if (car.transmission) schema.vehicleTransmission = car.transmission;
  if (car.color || car.exteriorColor) schema.color = car.color || car.exteriorColor;
  if (car.power) schema.vehicleEngine = {
    "@type": "EngineSpecification",
    "name": `${car.power} kW`
  };
  if (car.driveType) schema.driveWheelConfiguration = car.driveType;
  if (car.bodyType) schema.bodyType = car.bodyType;
  if (car.vin) schema.vehicleIdentificationNumber = car.vin;

  return schema;
}
